/**
 * Discovery Service - Server-side room discovery with company filtering
 * Provides a reliable source of truth for room lists when lobby is unstable
 */

import { getServerBaseUrl } from '@/utils/serverUrl'
import { withBearerAuth } from '@/utils/authHeaders'

export interface DiscoveryRoom {
  roomId: string
  name: string
  roomType: string
  members: number
  isDefaultRoom: boolean
  hasPassword: boolean
  mapId?: string
  companyId: string | null
  customRoomId?: string
  metadata: Record<string, any>
}

export interface DiscoveryResponse {
  success: boolean
  companyId: string | null
  count: number
  rooms: DiscoveryRoom[]
  error?: string
}

export class DiscoveryService {
  private baseUrl: string

  constructor() {
    this.baseUrl = getServerBaseUrl()
  }

  /**
   * Discover rooms filtered by company ID
   * REQUIRES companyId - strict company isolation
   */
  async discoverRooms(options: {
    companyId?: string | null
    includeCustom?: boolean
    includeDefault?: boolean
    limit?: number
  } = {}): Promise<DiscoveryResponse> {
    try {
      // Get companyId from options or try to get from user store
      let companyId = options.companyId
      
      // If companyId not provided, try to get from user store
      if (companyId === undefined || companyId === null || companyId === '') {
        try {
          // Try to get from Redux store
          const store = (window as any).__REDUX_STORE__ || (global as any).store
          if (store && store.getState) {
            const state = store.getState()
            companyId = state?.user?.userData?.companyId || state?.user?.company?.id || null
          }
        } catch (e) {
          console.warn('[DiscoveryService] Could not access store to get companyId:', e)
        }
      }
      
      // REQUIRE companyId - server will reject requests without it
      if (!companyId || companyId.trim() === '') {
        const errorMsg = 'companyId is required for room discovery. User must be assigned to a company.'
        console.error('[DiscoveryService]', errorMsg)
        return {
          success: false,
          companyId: null,
          count: 0,
          rooms: [],
          error: errorMsg
        }
      }
      
      const params = new URLSearchParams()
      params.set('companyId', companyId)
      if (options.includeCustom !== undefined) {
        params.set('includeCustom', String(options.includeCustom))
      }
      if (options.includeDefault !== undefined) {
        params.set('includeDefault', String(options.includeDefault))
      }
      if (options.limit !== undefined) {
        params.set('limit', String(options.limit))
      }

      // Add timeout to prevent hanging requests
      // Use AbortController for compatibility with older environments
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000)
      
      const response = await fetch(`${this.baseUrl}/api/lobby/rooms/discover?${params}`, {
        method: 'GET',
        headers: withBearerAuth({
          'Content-Type': 'application/json',
        }),
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)

      if (!response.ok) {
        throw new Error(`Discovery request failed: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      
      // Track success
      if (data.success) {
        const { telemetryService } = await import('./telemetryService')
        telemetryService.trackDiscoverySuccess(companyId, data.count)
      }
      
      return data
    } catch (error) {
      console.error('Discovery service error:', error)
      
      // Get companyId for error tracking
      let companyId = options.companyId
      if (companyId === undefined || companyId === null || companyId === '') {
        try {
          const store = (window as any).__REDUX_STORE__ || (global as any).store
          if (store && store.getState) {
            const state = store.getState()
            companyId = state?.user?.userData?.companyId || state?.user?.company?.id || null
          }
        } catch (e) {
          // Ignore
        }
      }
      
      // Track failure
      const { telemetryService } = await import('./telemetryService')
      telemetryService.trackDiscoveryFailure(companyId || null, error instanceof Error ? error.message : 'Unknown error')
      
      return {
        success: false,
        companyId: companyId || null,
        count: 0,
        rooms: [],
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Discover rooms for the current user's company
   * REQUIRES companyId - will return error if companyId is null/empty
   */
  async discoverCompanyRooms(companyId: string | null): Promise<DiscoveryResponse> {
    if (!companyId || companyId.trim() === '') {
      return {
        success: false,
        companyId: null,
        count: 0,
        rooms: [],
        error: 'companyId is required for room discovery'
      }
    }
    
    return this.discoverRooms({
      companyId,
      includeCustom: true,
      includeDefault: true,
      limit: 500
    })
  }

  /**
   * Discover all rooms (global view) - DEPRECATED
   * Strict company isolation means we can no longer discover all rooms
   * This method will return an error
   */
  async discoverAllRooms(): Promise<DiscoveryResponse> {
    console.warn('[DiscoveryService] discoverAllRooms is deprecated. Strict company isolation requires companyId.')
    return {
      success: false,
      companyId: null,
      count: 0,
      rooms: [],
      error: 'discoverAllRooms is not supported. Strict company isolation requires a companyId parameter.'
    }
  }
}

// Export singleton instance
export const discoveryService = new DiscoveryService()
